"use client"

import { useEffect } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { useUser } from "@clerk/nextjs"
import posthog from "posthog-js"
import { PostHogProvider } from "@posthog/react"
import * as posthogLib from "@/lib/posthog"
import { captureError } from "@/lib/posthog-error-utils"

// Initialize PostHog once
if (typeof window !== "undefined" && posthogLib.POSTHOG_KEY) {
  posthog.init(posthogLib.POSTHOG_KEY, {
    api_host: posthogLib.POSTHOG_HOST,
    capture_pageview: false, // We handle pageviews manually
    capture_pageleave: true,
    autocapture: true, // Enable automatic event capture (includes exceptions)
    session_recording: {
      maskAllInputs: true, // Apply maskInputFn globally
      maskInputOptions: {
        password: true, // Always mask passwords
      },
      maskInputFn: (text, element) => {
        // Unmask the proto-chat input (has class "proto-chat")
        if (element?.classList?.contains('proto-chat')) {
          return text
        }
        // Mask all other inputs
        return '*'.repeat(text.length)
      },
    },
    loaded: (ph: any) => {
      // Ensure window.posthog is set for feature flag hooks
      if (typeof window !== "undefined") {
        window.posthog = ph
      }
      if (process.env.NODE_ENV === "development") {
        console.log("PostHog loaded with error tracking")
      }
    },
  })
  // Also assign posthog to window.posthog for immediate access
  window.posthog = posthog as any

  // Global unhandled promise rejection handler
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    // Known posthog-js/rrweb bug: SecurityError thrown during replay teardown
    // when cross-origin iframes (Stripe, Clerk, etc.) are present.
    // See https://github.com/PostHog/posthog-js/issues/2533
    if (
      event.reason instanceof DOMException &&
      event.reason.name === 'SecurityError' &&
      String(event.reason.message).includes('removeEventListener')
    ) {
      return
    }

    // Capture unhandled promise rejections to PostHog
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason))
    
    captureError(error, {
      source: 'unhandled_promise_rejection',
      promise_rejection: true,
      reason: String(event.reason),
    })

    // Log to console as well for development
    if (process.env.NODE_ENV === "development") {
      console.error('Unhandled promise rejection:', event.reason)
    }
  })
}

function PageViewTracker() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { user, isLoaded } = useUser()

  // Identify user from Clerk
  useEffect(() => {
    if (posthogLib.POSTHOG_KEY && typeof window !== "undefined" && isLoaded) {
      if (user && posthog && typeof posthog.identify === 'function') {
        // Identify the user in PostHog with Clerk user data
        posthog.identify(user.id, {
          email: user.primaryEmailAddress?.emailAddress,
          name: user.fullName,
          firstName: user.firstName,
          lastName: user.lastName,
          createdAt: user.createdAt,
        })
        if (process.env.NODE_ENV === "development") {
          console.log("PostHog user identified from Clerk:", user.id)
        }
      } else if (!user && posthog && typeof posthog.reset === 'function') {
        // User signed out, reset PostHog
        posthog.reset()
        if (process.env.NODE_ENV === "development") {
          console.log("PostHog reset - user signed out")
        }
      }
    }
  }, [user, isLoaded])

  // Identify user from uid URL parameter (fallback for non-authenticated tracking)
  useEffect(() => {
    if (posthogLib.POSTHOG_KEY && typeof window !== "undefined") {
      const uid = searchParams.get('uid')
      if (uid && posthog && typeof posthog.identify === 'function') {
        // Identify the user in PostHog with the uid from URL parameter
        posthog.identify(uid, {
          source: 'url_parameter',
        })
        if (process.env.NODE_ENV === "development") {
          console.log("PostHog user identified from URL:", uid)
        }
      }
    }
  }, [searchParams])

  useEffect(() => {
    if (posthogLib.POSTHOG_KEY && typeof window !== "undefined" && pathname) {
      // Capture pageview on initial mount
      posthog.capture('$pageview')
    }
  }, [])

  // Track pageview on route change
  useEffect(() => {
    if (posthogLib.POSTHOG_KEY && typeof window !== "undefined" && pathname) {
      // Only capture if PostHog is initialized (check if capture method exists)
      if (posthog && typeof posthog.capture === 'function') {
        posthog.capture('$pageview')
      }
    }
  }, [pathname])

  return null
}

export function PostHog() {
  if (!posthogLib.POSTHOG_KEY) {
    return null
  }

  return (
    <PostHogProvider client={posthog}>
      <PageViewTracker />
    </PostHogProvider>
  )
}
